import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { listRepos, reindexRepo } from '../api/repos.js';
import type { RepoEntry } from '../api/types.js';
import { useApi } from '../api/use-api.js';

import { RepoCard } from './repo-card.js';
import { EmptyRepos, JobDrawer, PageState } from './repo-list-states.js';

export function RepoList(): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const [activeJob, setActiveJob] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['repos'],
    queryFn: () => listRepos(api),
    refetchInterval: 30_000,
  });

  const reindex = useMutation({
    mutationFn: (slug: string) => reindexRepo(api, slug),
    onSuccess: (jobId) => setActiveJob(jobId),
  });

  if (isLoading) {
    return (
      <PageState icon={<Loader2 className="h-5 w-5 animate-spin" />} text="Loading repositories…" />
    );
  }
  if (error) {
    return <PageState text={`Failed to load repos: ${String(error)}`} tone="error" />;
  }
  const repos: readonly RepoEntry[] = data ?? [];

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
      <header className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
            Workspace
          </span>
          <h1 className="bg-gradient-to-r from-white via-text-primary to-text-secondary bg-clip-text text-3xl font-bold tracking-tight text-transparent">
            Repositories
          </h1>
          <p className="text-sm text-text-secondary">
            {repos.length} indexed · click a row to open the graph.
          </p>
        </div>
      </header>

      {repos.length === 0 ? (
        <EmptyRepos />
      ) : (
        <ul className="grid gap-3">
          {repos.map((repo) => (
            <RepoCard
              key={repo.slug}
              repo={repo}
              reindexing={reindex.isPending && reindex.variables === repo.slug}
              onReindex={() => reindex.mutate(repo.slug)}
            />
          ))}
        </ul>
      )}

      {activeJob && (
        <JobDrawer
          jobId={activeJob}
          onClose={() => setActiveJob(null)}
          onTerminal={() => {
            void queryClient.invalidateQueries({ queryKey: ['repos'] });
          }}
        />
      )}
    </section>
  );
}
